import {
  MultipleSearchParams,
  RangeSearchParams,
  SearchCriteria,
  SearchField,
  SearchParams,
  SearchRange,
  SearchRequest,
  SearchRequests,
  SearchValues,
  SingleSearchParams,
} from '@/components/search/types';

/** Helper to create searchCriteria. */
export const searchCriteria = {
  values(veld: SearchField, waarden: string[] | number[] | string | number): SearchValues {
    return {
      veld,
      waarden: Array.isArray(waarden) ? waarden : ([waarden] as string[] | number[]),
    };
  },
  range(veld: SearchField, van: string | number, totEnMet: string | number): SearchRange {
    const range = {
      veld,
      van,
      totEnMet,
    };
    return range;
  },
};

/**
 * This function returns a singular SearchRequest, therefor the combined searchType 'trainAndRollingStock' should be split up into
 * 'train' and 'rollingStock'
 * This function explicitly does not handle the case of certain searchType's / searchModalities, see searchParamsRequests.
 *
 * @param searchParams
 * @returns
 */
function transformSearchParamsToRequest(searchParams: SearchParams): SearchRequest {
  const { searchType, searchModality } = searchParams;

  const criteria: SearchCriteria[] = [];
  // Create a query for values.
  const valueQuery = () => {
    const { searchValue } = searchParams as SingleSearchParams | MultipleSearchParams;
    if (searchType === 'train') criteria.push(searchCriteria.values('beweging.treinnummer', searchValue));
    if (searchType === 'rollingStock') criteria.push(searchCriteria.values('eenheid.nummer', searchValue));
    if (searchType === 'rollingStockType') criteria.push(searchCriteria.values('materieelsoort', searchValue));
    if (searchType === 'omloop') criteria.push(searchCriteria.values('omloop.dienstcode', searchValue));
    if (searchType === 'claimsoort') criteria.push(searchCriteria.values('claim.soort', searchValue));
    if (searchType === 'bijzonderheidscode') criteria.push(searchCriteria.values('*', searchValue));
    if (searchType === 'empty') criteria.push(searchCriteria.values('*', searchValue));
  };
  if (['single', 'multiple'].includes(searchModality)) valueQuery();

  // Create a range query
  const rangeQuery = () => {
    const rangeParams = searchParams as RangeSearchParams;
    const [van, totEnMet] = [rangeParams?.fromValue, rangeParams?.toValue] as number[];
    if (searchType === 'train') criteria.push(searchCriteria.range('beweging.treinnummer', van, totEnMet));
    if (searchType === 'empty') criteria.push(searchCriteria.range('*', van, totEnMet));
    if (searchType === 'rollingStock') criteria.push(searchCriteria.range('eenheid.nummer', van, totEnMet));
  };
  if (searchModality === 'range') rangeQuery();

  // Optionally add date to the query.
  const dateQuery = () => {
    const searchDate = (searchParams as SingleSearchParams | MultipleSearchParams | RangeSearchParams)?.searchDate;
    if (!searchDate) return;
    if (searchType === 'train') criteria.push(searchCriteria.values('beweging.treindatum', searchDate));
    if (searchType === 'omloop') criteria.push(searchCriteria.values('omloop.dienstdatum', searchDate));
    if (['claimsoort', 'bijzonderheidscode', 'empty'].includes(searchType))
      criteria.push(searchCriteria.values('verkeersdatum', searchDate));
  };

  dateQuery();

  // If empty: appearantly gives back all worklines.
  return { criteria };
}

/**
 * Transform the current SearchParams to the v2 API.
 * @param searchParams
 * @returns
 */
export default function searchParamsRequests(searchParams: SearchParams | null | undefined): SearchRequests {
  if (!searchParams) return [];
  const { searchType, searchModality } = searchParams;
  if (searchModality === 'json') {
    // Value should already be validated at this point.
    const parsedSearchValue = JSON.parse(searchParams.searchValue);
    return Array.isArray(parsedSearchValue) ? parsedSearchValue : [parsedSearchValue];
  }

  if (searchType === 'trainAndRollingStock') {
    return [
      transformSearchParamsToRequest({ ...searchParams, searchType: 'train' }),
      transformSearchParamsToRequest({ ...searchParams, searchType: 'rollingStock' }),
    ];
  }
  return [transformSearchParamsToRequest(searchParams)];
}
